// Streamed answers reach the page as Firestore snapshots about once a second. This reveals the new
// text at a steady pace so the answer types out instead of jumping a paragraph at a time.
// Only growth after the message is first shown is animated (an answer opened mid-stream or already
// complete appears as it is), and nothing is animated when the user prefers reduced motion.
import { useEffect, useRef, useState } from 'react';

const CATCH_UP_MS = 900;     // close any gap within about one snapshot interval
const MIN_CHARS_PER_SEC = 60;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function useSmoothText(text, animate) {
  const [shown, setShown] = useState(() => text.length);
  const shownRef = useRef(shown);
  const frame = useRef(null);

  useEffect(() => {
    if (!animate || prefersReducedMotion() || text.length < shownRef.current) {
      shownRef.current = text.length;
      setShown(text.length);
      return undefined;
    }
    let last = performance.now();
    const gap = text.length - shownRef.current;
    const speed = Math.max(gap / (CATCH_UP_MS / 1000), MIN_CHARS_PER_SEC);
    const step = t => {
      const next = Math.min(text.length, shownRef.current + Math.max(1, Math.round(((t - last) / 1000) * speed)));
      last = t;
      shownRef.current = next;
      setShown(next);
      if (next < text.length) frame.current = requestAnimationFrame(step);
    };
    if (gap > 0) frame.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame.current);
  }, [text, animate]);

  return { text: text.slice(0, shown), done: shown >= text.length };
}
